import {
	ActionListBusyIndicator,
	ActionListItem,
	BusyIndicator,
	DropOverActionList,
	FontIconName,
	ListItem,
	Portal,
	SvgIconValue
} from "client/Components";
import { contains } from "client/Data/searching";
import { TeamsAction } from "client/Api/Apps/EditApp/saveApp";
import {
	AlertManager,
	createLocalDataSource,
	useAlerts,
	useToggle
} from "client/Hooks";
import {
	ensureNotNil,
	hasFlag,
	generateTrackingId
} from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { localize } from "client_legacy/ndaw/culture";
import { newGuid } from "client_legacy/ndaw/guid";
import { isAndroid, isIos } from "client_legacy/ndaw/mobile";
import { navigateTo } from "client_legacy/ndaw/navigator";
import { TeamsProvider } from "./provider";
import {
	TeamDetailsViewModel,
	TeamListItemViewModel
} from "client/Api/Workspace/LiveInformation/Teams/teamsLiveInformation";
import {
	ReactElement,
	RefObject,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { LiveTileContent, TileScrollableHandle } from "../LiveTileContent";
import { TileContext } from "../TileContext";
import { useAddAnalyticsAppEvent } from "client/Features/Analytics/useAddAnalyticsAppEvent";
import { getSharePointLocationUrl } from "client/Features/Documents/routing";
import { ActionListNoResults } from "client/Components/ActionList/ActionListNoResults";
import { LiveTileOverlayState, useLiveTileOverlayState } from "../useLiveTileOverlayState";
import { Coordinate } from "client/Misc/commonTypes";
import { TileStatus } from "../TileStatus";

export type TeamsLiveTileContentProps = {
	provider: TeamsProvider;
	enabledActions: TeamsAction;
	onNavigate: () => void;
	onOpenTileMenu: (coordinate: Coordinate) => void;
};

export const TeamsLiveTileContent = (props: TeamsLiveTileContentProps): ReactElement => {
	const {
		provider,
		enabledActions,
		onNavigate,
		onOpenTileMenu
	} = props;

	const {
		icon,
		name,
		environmentAppId,
		interactionState,
		dimension,
		appIsLicensed,
		appIsNotAccessible
	} = useContext(TileContext);

	const {
		addEvent: addAnalyticsEvent
	} = useAddAnalyticsAppEvent(environmentAppId);

	const overlayState: LiveTileOverlayState = useLiveTileOverlayState({
		interactionState,
		dimension,
		appIsLicensed,
		appIsNotAccessible
	});

	const navigateHandler = useCallback(
		(): void => {
			addAnalyticsEvent();
			onNavigate();
		},
		[addAnalyticsEvent, onNavigate]
	);

	return (
		<LiveTileContent
			icon={icon}
			name={name}
			overlay={overlayState}
			onOpenTileMenu={onOpenTileMenu}
			overlayStatusIcon={<TileStatus />}
		>
			<LiveTileContent.Header icon={icon}>
				<LiveTileContent.Title text={name} />
			</LiveTileContent.Header>

			<LiveTileContent.Content>
				<ListView
					provider={provider}
					enabledActions={enabledActions}
					onAddAnalyticsEvent={addAnalyticsEvent}
				/>
			</LiveTileContent.Content>

			<LiveTileContent.Footer
				name={name}
				onClick={navigateHandler}
			/>
		</LiveTileContent>
	);
};
TeamsLiveTileContent.displayName = "TeamsLiveTileContent";

type ItemsLoadingState = { isLoading: true; }
	| { isLoading: false; } & (
		{ hasError: false; items: readonly TeamListItemViewModel[]; }
		| { hasError: true; }
	);

export type ListViewProps = {
	provider: TeamsProvider;
	enabledActions: TeamsAction;
	onAddAnalyticsEvent: () => void;
};

export const ListView = (props: ListViewProps): ReactElement => {
	const {
		provider,
		enabledActions,
		onAddAnalyticsEvent
	} = props;

	const [searchTerm, setSearchTerm] = useState<VersionedValue<string>>({
		value: "",
		version: newGuid()
	});

	const [loadingState, setLoadingState] = useState<ItemsLoadingState>({ isLoading: true });
	const scrollableRef: RefObject<TileScrollableHandle | null> = useRef<TileScrollableHandle>(null);

	const alertManager: AlertManager = useAlerts();

	const { currentItems } = useDataSource(
		!loadingState.isLoading && !loadingState.hasError
			? loadingState.items
			: [],
		{
			searchTerm: searchTerm.value
		}
	);

	useEffect(
		(): void => {
			hasImplicitDependencyOn(searchTerm.value);

			scrollableRef.current
				?.scrollTo(0);
		},
		[searchTerm.value]
	);

	useEffect(
		(): (() => void) | void => {

			if (!provider.isAvailable) {
				return;
			}

			const loadList = async (signal: AbortSignal): Promise<void> => {
				setLoadingState({ isLoading: true });

				try {
					const items: readonly TeamListItemViewModel[] = await provider.list();

					if (signal.aborted) {
						return;
					}

					setLoadingState({
						isLoading: false,
						hasError: false,
						items
					});

				} catch (e) {
					setLoadingState({
						isLoading: false,
						hasError: true
					});
				}
			};

			const cancellation = new AbortController();
			loadList(cancellation.signal);
			return () => cancellation.abort();
		},
		[provider]
	);

	const renderContent = (): ReactElement => {
		if (!provider.isAvailable) {
			return <LiveTileContent.Message text={localize("_OAuth2IsNotConfiguredAsSingleSignOnTypeThereforeWeCouldNotRetrieveYourTeams")} />;
		}

		if (loadingState.isLoading) {
			return <BusyIndicator />;
		}

		if (loadingState.hasError) {
			return <LiveTileContent.Message text={localize("_UnableToRetrieveItems")} />;
		}

		const hasAnyTeams: boolean = loadingState.items
			.length > 0;

		if (!hasAnyTeams) {
			return <LiveTileContent.Message text={localize("_YouDontHaveAnyTeams")} />;
		}

		if (currentItems.length === 0) {
			return <LiveTileContent.Message text={localize("_NoResultsFound")} />;
		}

		return (
			<LiveTileContent.Scrollable ref={scrollableRef}>
				<LiveTileContent.List>
					{currentItems.map((x) => (
						<TeamListItem
							key={x.id}
							model={x}
							provider={provider}
							enabledActions={enabledActions}
							onAddAnalyticsEvent={onAddAnalyticsEvent}
							alertManager={alertManager}
						/>
					))}
				</LiveTileContent.List>
			</LiveTileContent.Scrollable>
		);
	};

	return (
		<LiveTileContent.ContentStack>
			{provider.isAvailable && (
				<LiveTileContent.SearchInput
					value={searchTerm}
					onChange={(value, version) => setSearchTerm({ value, version })}
					trackingId="search-term"
					placeholder={localize("_Search")}
				/>
			)}
			<LiveTileContent.BusyIndicatorContainer>
				{renderContent()}
			</LiveTileContent.BusyIndicatorContainer>
		</LiveTileContent.ContentStack>
	);
};
ListView.displayName = "ListView";

const useDataSource = createLocalDataSource<TeamListItemViewModel>()
	.setConfiguration({
		search: {
			doesMatchItem: (item, searchTerm) =>
				contains(item.displayName, searchTerm)
		},
	})
	.getHook();

type TeamListItemProps = {
	model: TeamListItemViewModel;
	provider: TeamsProvider;
	enabledActions: TeamsAction;
	onAddAnalyticsEvent: () => void;
	alertManager: AlertManager;
};

const TeamListItem = (props: TeamListItemProps): ReactElement => {
	const {
		model,
		provider,
		enabledActions,
		onAddAnalyticsEvent,
		alertManager
	} = props;

	const { id, displayName } = model;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [areActionsOpen, toggleAreActionsOpen] = useToggle();
	const actionsRef: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement | null>(null);

	const navigateHandler = useCallback(
		async (): Promise<void> => {
			onAddAnalyticsEvent();
			try {
				setIsBusy(true);
				const details: TeamDetailsViewModel = await provider.details(id);

				const availableActions: readonly DetailsAction[] = listAvailableActions(details, enabledActions);

				if (availableActions.length > 0) {
					availableActions[0].execute();
					return;
				}
			} catch (e) {
				console.error(e);
			} finally {
				setIsBusy(false);
			}

			alertManager.showError(localize("_GeneralErrorMessage"));
		},
		[
			alertManager,
			enabledActions,
			id,
			onAddAnalyticsEvent,
			provider
		]
	);

	const trackingId: string = generateTrackingId("item", `|${id}`);

	return (
		<>
			{isBusy && (
				<Portal container={document.body}>
					<BusyIndicator />
				</Portal>
			)}

			<ListItem
				noMobileBorderBottom
				trackingId={trackingId}
			>
				<ListItem.Content onClick={navigateHandler}>
					<ListItem.Title>
						{displayName}
					</ListItem.Title>
				</ListItem.Content>

				<ListItem.Action
					icon="ellipsis"
					ref={actionsRef}
					onClick={toggleAreActionsOpen}
					trackingId="actions"
					isActive={areActionsOpen}
				/>

				{areActionsOpen && (
					<TeamActionsDropOver
						id={id}
						displayName={displayName}
						provider={provider}
						enabledActions={enabledActions}
						onAddAnalyticsEvent={onAddAnalyticsEvent}
						target={ensureNotNil(actionsRef.current)}
						onClose={() => toggleAreActionsOpen(false)}
						trackingId={generateTrackingId(trackingId, "__actions-dropover")}
					/>
				)}
			</ListItem>
		</>
	);
};
TeamListItem.displayName = "TeamListItem";

type TeamActionsDropOverProps = {
	id: string;
	displayName: string;
	provider: TeamsProvider;
	enabledActions: TeamsAction;
	onAddAnalyticsEvent: () => void;
	target: HTMLElement;
	onClose: () => void;
	trackingId: string;
};

const TeamActionsDropOver = (props: TeamActionsDropOverProps): ReactElement => {
	const {
		id,
		displayName,
		provider,
		enabledActions,
		onAddAnalyticsEvent,
		target,
		onClose,
		trackingId
	} = props;

	const [loadingState, setLoadingState] = useState<DetailsLoadingState>({ isLoading: true });

	useEffect(
		(): (() => void) => {
			const loadDetails = async (id: string, signal: AbortSignal): Promise<void> => {
				setLoadingState({ isLoading: true });

				try {
					const details: TeamDetailsViewModel = await provider.details(id);

					if (signal.aborted) {
						return;
					}

					setLoadingState({
						isLoading: false,
						hasError: false,
						details
					});

				} catch (e) {
					setLoadingState({
						isLoading: false,
						hasError: true
					});
				}
			};

			const cancellation = new AbortController();
			loadDetails(id, cancellation.signal);
			return () => cancellation.abort();
		},
		[id, provider]
	);

	const actions = useMemo(
		(): readonly DetailsAction[] => {
			if (loadingState.isLoading || loadingState.hasError) {
				return [];
			}

			return listAvailableActions(loadingState.details, enabledActions);
		},
		[enabledActions, loadingState]
	);

	const executeActionHandler = useCallback(
		(action: DetailsAction): void => {
			onAddAnalyticsEvent();
			action.execute();
		},
		[onAddAnalyticsEvent]
	);

	const renderContent = (): ReactElement => {
		if (loadingState.isLoading) {
			return <ActionListBusyIndicator trackingId="loading" />;
		}

		if (loadingState.hasError) {
			return (
				<ActionListNoResults
					trackingId="error"
					icon="gear"
					title={localize("_UnableToRetrieveItems")}
					message={localize("_WeWereUnableToRetrieveTheItems")}
				/>
			);
		}

		if (actions.length === 0) {
			return (
				<ActionListNoResults
					trackingId="no-items"
					icon="list"
					title={localize("_NoItemsToShow")}
					message={localize("_ThereAreCurrentlyNoItemsToShowHere")}
				/>
			);
		}

		return (
			<>
				{actions.map((x): ReactElement => (
					<ActionListItem
						key={x.name}
						trackingId={x.name}
						icon={x.icon}
						text={x.text}
						onClick={() => executeActionHandler(x)}
					/>
				))}
			</>
		);
	};

	return (
		<DropOverActionList
			target={target}
			onClose={onClose}
			trackingId={trackingId}
			menuTitle={displayName}
			targetAlignment="br"
			contentAlignment="tr"
		>
			{renderContent()}
		</DropOverActionList>
	);
};
TeamActionsDropOver.displayName = "TeamActionsDropOver";

function listAvailableActions(
	details: TeamDetailsViewModel,
	enabledActions: TeamsAction
): readonly DetailsAction[] {
	const {
		webUrl,
		sharePoint
	} = details;

	const {
		web = null,
		localApp = null
	} = getLaunchUris(webUrl);

	const result: DetailsAction[] = [];

	if (hasFlag(enabledActions, TeamsAction.OpenWebClient) && web) {
		result.push({
			name: "teams-online",
			text: localize("_OpenInAppName")
				.replace("{0}", localize("_TeamsOnline")),
			icon: { kind:"svg", name:"ms-teams"},
			execute: () => navigateTo(web, true)
		});
	}

	if (hasFlag(enabledActions, TeamsAction.OpenApp) && localApp) {
		result.push({
			name: "teams-desktop",
			text: localize("_OpenInAppName")
				.replace("{0}", localize("_Teams")),
			icon: { kind:"svg", name:"ms-teams"},
			execute: () => navigateTo(localApp)
		});
	}

	if (sharePoint) {
		const { location } = sharePoint;

		if (location && hasFlag(enabledActions, TeamsAction.OpenLibraryInDocumentsApp)) {
			result.push({
				name: "dms",
				text: localize("_OpenDocuments"),
				icon: "folder-open",
				execute: () => {
					const url: string = getSharePointLocationUrl(location);
					navigateTo(url);
				}
			});
		}
	}

	return result;
}

type DetailsLoadingState = { isLoading: true; }
	| { isLoading: false; } & (
		{ hasError: false; details: TeamDetailsViewModel; }
		| { hasError: true; }
	);

type DetailsAction = {
	name: string;
	text: string;
	icon: FontIconName | SvgIconValue;
	execute: () => void;
};

// We expect to get a "launcher url" from the back
// https://teams.microsoft.com/l/team/19%3a1f22963d542b468c83182c7b78642bdd%40thread.tacv2/conversations?groupId=51081ca6-5aa2-4af6-ac46-d656c0c02323&tenantId=892f5ba9-07a2-463e-9583-50a00ac7ba2a
// The app uri created by the "launcher"
// msteams:/l/team/19:1f22963d542b468c83182c7b78642bdd@thread.tacv2/conversations?groupId=51081ca6-5aa2-4af6-ac46-d656c0c02323&tenantId=892f5ba9-07a2-463e-9583-50a00ac7ba2a&deeplinkId=161cb9c0-74cf-4a3a-9151-180f88f0262c&launchAgent=join_launcher&type=team&directDl=true&msLaunch=true&enableMobilePage=true&suppressPrompt=true&fqdn=teams.microsoft.com
function getLaunchUris(webUrl: string): { web: string | null; localApp: string | null; } {
	if (!webUrl) {
		return {
			web: null,
			localApp: null
		};
	}

	return {
		web: buildOnlineUrl(webUrl),
		localApp: buildLocalAppUrl(webUrl)
	};
}

function buildOnlineUrl(webUrl: string): string {
	const url = new URL(webUrl);

	url.searchParams
		.set("webjoin", "true");

	return url.toString();
}

function buildLocalAppUrl(webUrl: string): string {
	const url = new URL(webUrl);
	const webUrlWithoutAuthority: string = url.pathname + url.search + url.hash;

	if (isIos() || isAndroid()) {
		// on mobile os's they use deep linking (or universal linking called in apple world) which basically
		// means to prefix the url with the hostname
		return "msteams://" + url.hostname + webUrlWithoutAuthority;
	} else {
		return "msteams:" + webUrlWithoutAuthority;
	}
}
